import { clientHues, byKey as byKeyDefault } from '../lib/variant-hues.js';

/* Recorded profile chips. The page owns loading, client filtering and selection;
   this component only presents the profilesView result and emits profile ids. */
(function () {
  const { useState, useRef, useEffect, useMemo } = React;
  const DAY = 864e5;

  function age(iso) {
    if (!iso) return null;
    const elapsed = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(elapsed)) return null;
    const d = Math.max(0, elapsed / DAY);
    const t = d < 1 ? 'today' : d < 14 ? Math.round(d) + 'd ago' : d < 60 ? Math.round(d / 7) + 'w ago' : Math.round(d / 30) + 'mo ago';
    return { t, stale: d > 90 };
  }

  function ClientVariants({ clientName, clientSlug, view, pendingCount = null, valueId, onSelect, applies,
    hues = clientHues, byKey = byKeyDefault, onDetails }) {
    const [drawer, setDrawer] = useState(false);
    const previousClient = useRef(clientSlug);
    const clientChanged = previousClient.current !== clientSlug;
    const state = view.state;
    const ready = state === 'ready' ? view.ready : [];
    const degraded = view.degraded || [];
    const selectedId = clientChanged ? '' : (valueId || '');
    const chosen = ready.find((p) => p.profile_id === selectedId) || null;
    const hueMap = useMemo(() => hues(ready.map((p) => ({ id: p.profile_id, status: 'ready' }))), [ready, hues, clientSlug]);
    const profileHue = (p) => hueMap[p.profile_id] || byKey('');
    const hueStyle = (h) => ({ '--vh': h.dot, '--vh-ink': h.ink, '--vh-tint': h.tint });
    const name = (p) => (p.scope && p.scope.variant_id) || p.profile_id;

    /* Clear the parent's controlled selection once when the client changes. */
    useEffect(() => {
      if (previousClient.current !== clientSlug) {
        previousClient.current = clientSlug;
        onSelect('');
      }
    }, [clientSlug, onSelect]);
    useEffect(() => { setDrawer(false); }, [clientSlug, valueId]);

    const policies = chosen && Array.isArray(chosen.region_policies) ? chosen.region_policies : [];
    const count = chosen && (chosen.region_count == null ? policies.length : chosen.region_count);
    const esp = chosen && chosen.scope && chosen.scope.esp_id;

    return (
      <div className="cv-block" data-variants-state={state}>
        <div className="cv-head">
          <span className="cv-title"><I.layers size={13} /> {clientName} variants</span>
          <span className="cv-count">
            {state === 'ready' ? <b>{ready.length} ready</b> : state === 'none' ? <span>none ready</span> : null}
            {pendingCount > 0 && <span className="cv-pend">{pendingCount} pending</span>}
          </span>
        </div>

        {state === 'loading' && <p className="cv-empty" role="status">Loading recorded variants…</p>}
        {state === 'unavailable' && <p className="cv-empty">Variant profiles are unavailable on this server.</p>}
        {state === 'error' && <p className="cv-empty" role="alert">Could not load variant profiles.</p>}
        {state === 'none' && <p className="cv-empty">No recorded variants for {clientName} yet — this build uses the client's default profile.</p>}
        {degraded.length > 0 && <p className="cv-empty">{degraded.length} {degraded.length === 1 ? 'profile is' : 'profiles are'} unavailable.</p>}
        {!applies && <p className="cv-empty">Variant profiles apply to Sabre builds only</p>}

        {state === 'ready' &&
          <React.Fragment>
            <div className="cv-chips">
              <button type="button" className={'cv-chip' + (!selectedId ? ' sel' : '')}
                data-variant-profile="" data-hue="" aria-pressed={!selectedId} onClick={() => onSelect('')}>
                <span className="cv-chip-n">Client default{!selectedId && <I.check size={12} />}</span>
                <span className="cv-chip-m">client default profile</span>
              </button>
              {ready.map((p) => {
                const a = age(p.activated_at);
                const on = chosen === p;
                const h = profileHue(p);
                return (
                  <button type="button" key={p.profile_id} className={'cv-chip cv-hue' + (on ? ' sel' : '')}
                    data-variant-profile={p.profile_id} data-hue={h.k} aria-pressed={on}
                    onClick={() => onSelect(p.profile_id)} title={p.profile_id} style={hueStyle(h)}>
                    <span className="cv-chip-n">
                      <i className="cv-dot" aria-hidden="true"></i>
                      <span className="mono">{name(p)}</span>
                      {on && <I.check size={12} />}
                    </span>
                    <span className="cv-chip-m">
                      {p.scope && p.scope.sub_brand_id && <span className="cv-sub">{p.scope.sub_brand_id}</span>}
                      {a && <span className={'cv-age' + (a.stale ? ' stale' : '')}
                        title={'Activated ' + a.t + (a.stale ? ' — check it still matches what the client ships' : '')}>activated {a.t}</span>}
                    </span>
                  </button>);
              })}
            </div>

            {chosen &&
              <div className="vp2-ctx" key={chosen.profile_id} data-building-from={chosen.profile_id}>
                <div className="cv-from" style={hueStyle(profileHue(chosen))}>
                  <span className="cv-from-wash" aria-hidden="true"></span>
                  <i className="cv-from-dot" aria-hidden="true"></i>
                  <span className="cv-from-t">
                    <em>Building from</em>
                    <b>{name(chosen)}</b>
                  </span>
                  <span className="cv-from-m">{count} region {count === 1 ? 'rule' : 'rules'}{chosen.container_width != null && ' · ' + chosen.container_width + 'px shell'}</span>
                  {typeof onDetails === 'function' &&
                    <button type="button" className="cv-link" onClick={() => onDetails(chosen.profile_id)}
                      title={'Open ' + name(chosen) + ' in the Sabre profile panel'}>
                      Details <I.chevron size={11} />
                    </button>}
                </div>
                {chosen.container_width != null &&
                  <div className="vp2-row">
                    <span className="vp2-k">Viewport</span>
                    <span className="vp2-chips">
                      <span className="vp2-chip">desktop <b>{chosen.container_width}px</b></span>
                    </span>
                  </div>}

                <div className="vp2-row">
                  <span className="vp2-k">Regions</span>
                  <span className="vp2-chips">
                    <span className="vp2-chip">{count} region {count === 1 ? 'rule' : 'rules'}</span>
                    <button type="button" className="vp2-link" aria-expanded={drawer} onClick={() => setDrawer((d) => !d)}>
                      {drawer ? 'Hide' : 'View'} <I.chevron size={11} style={{ transform: drawer ? 'rotate(-90deg)' : 'rotate(90deg)' }} />
                    </button>
                  </span>
                </div>
                {drawer &&
                  <div className="vp2-drawer">
                    {policies.map((p, i) =>
                      <div className="vp2-cl" key={(p.semantic_role || 'region') + '-' + i}>
                        <span className="vp2-cl-t"><b>{p.semantic_role}</b>{p.renderer_recipe && ' · ' + p.renderer_recipe}</span>
                      </div>)}
                    {!policies.length && <span className="muted">{Array.isArray(chosen.region_policies)
                      ? 'No region rules recorded for this profile.' : 'Region rules are not included in this profile summary.'}</span>}
                  </div>}

                <div className="vp2-row vp2-esp">
                  <span className="vp2-k">ESP binding<span className="v-postpass">read-only</span></span>
                  {esp ?
                    <span className="vp2-chips"><span className="vp2-chip"><b>{esp}</b></span></span> :
                    <span className="muted" style={{ fontSize: 12 }}>No ESP binding recorded for this profile.</span>}
                </div>
              </div>}
          </React.Fragment>}
      </div>);
  }

  window.ClientVariants = ClientVariants;
})();
